/**
 * Telemetria própria do site → edge function `site-ingest` (Supabase do CRM).
 * Só dispara depois do aceite no banner LGPD. Sem cookie, sem PII: usa o
 * `sid` do localStorage (mesmo da atribuição).
 *
 * Fila com flush por lote via navigator.sendBeacon (sobrevive ao fechar a aba).
 */
import { getConsent } from "./analytics";
import { attributionParams, attributionSummary, getSid } from "./attribution";

const ENDPOINT = import.meta.env.VITE_TELEMETRY_URL || "";

type Ev = {
  tipo: string;
  path?: string;
  secao?: string;
  label?: string;
  valor?: number;
  meta?: Record<string, unknown>;
  seq: number;
  ts: string;
};

let seq = 0;
let queue: Ev[] = [];
let sessionSent = false;
let flushTimer: ReturnType<typeof setTimeout> | undefined;

function enabled(): boolean {
  return !!ENDPOINT && getConsent() === "granted" && typeof navigator !== "undefined";
}

function sessionPayload() {
  const p = attributionParams();
  return {
    session_id: getSid(),
    entry_path: location.pathname + location.search,
    referrer: document.referrer || "",
    canal_origem: canalFrom(attributionSummary(), p),
    utm_source: p.utm_source || "",
    utm_medium: p.utm_medium || "",
    utm_campaign: p.utm_campaign || "",
    gclid: p.gclid || p.wbraid || p.gbraid || "",
    tz_offset: new Date().getTimezoneOffset(),
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
    lang: navigator.language || "",
    ua: navigator.userAgent || "",
  };
}

function canalFrom(summary: string, p: Record<string, string>): string {
  if (p.gclid || p.gbraid || p.wbraid || /google/.test(summary)) return "google";
  if (p.fbclid || /meta|facebook|instagram/.test(summary)) return "meta";
  if (summary.startsWith("referência")) return "referral";
  if (summary === "direto") return "direto";
  return "organico";
}

function send(body: unknown, beacon = false): void {
  const json = JSON.stringify(body);
  try {
    if (beacon && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([json], { type: "application/json" }));
      return;
    }
  } catch {
    /* cai no fetch */
  }
  void fetch(ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: json,
    keepalive: true,
    mode: "cors",
  }).catch(() => {});
}

export function flush(beacon = false): void {
  if (!enabled() || (!queue.length && sessionSent)) return;
  const batch = queue;
  queue = [];
  const payload: Record<string, unknown> = { session_id: getSid(), events: batch };
  if (!sessionSent) {
    payload.session = sessionPayload();
    sessionSent = true;
  }
  send(payload, beacon);
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = undefined;
    flush();
  }, 4000);
}

/** Registra um evento. `now=true` faz flush imediato (cliques de conversão). */
export function track(
  tipo: string,
  data: Omit<Ev, "tipo" | "seq" | "ts"> = {},
  now = false,
): void {
  if (!enabled()) return;
  queue.push({ tipo, ...data, seq: seq++, ts: new Date().toISOString() });
  if (now || queue.length >= 20) flush();
  else scheduleFlush();
}

export const tPageView = (path: string) => track("pageview", { path });
export const tSection = (secao: string) => track("section", { secao });
export const tScroll = (pct: number) => track("scroll", { valor: pct });
export const tCta = (label: string, meta?: Record<string, unknown>) =>
  track("cta", { label, meta }, true);
export const tWhatsapp = (label?: string) => track("whatsapp", { label }, true);
export const tHuggy = (label?: string) => track("huggy", { label }, true);
export const tForm = (label: string) => track("form", { label }, true);

/** Liga os listeners de ciclo de vida (flush ao sair). Idempotente. */
let started = false;
export function startTelemetry(): void {
  if (started || typeof window === "undefined") return;
  started = true;

  const onHidden = () => {
    if (document.visibilityState === "hidden") {
      track("exit", { path: location.pathname, valor: Math.round(performance.now() / 1000) });
      flush(true);
    }
  };
  document.addEventListener("visibilitychange", onHidden);
  window.addEventListener("pagehide", () => flush(true));
}
